
import {
  defineComponent,
  ref,
  computed,
  defineAsyncComponent,
} from 'vue';

import Dialog from 'primevue/dialog';

import { ServiceParameter } from '@/types/store-parameters-types';
import { getFeatureFlagForService } from '@/configs/feature-flags';
import ComingSoonBadge from '@/components/shared/ComingSoonBadge.vue';

import { useI18n } from 'vue-i18n';

interface ServiceGroup {
  title: string;
  description: string;
  icon: string;
  services: ServiceParameter[];
}

export default defineComponent({
  name: 'NewServicesParameters',

  components: {
    Dialog,
    ComingSoonBadge,
  },

  props: {
    shopId: {
      type: Number,
      required: true,
    },

    shopUrl: {
      type: String,
      required: true,
    },
  },

  setup(props) {
    const { t } = useI18n();

    const displayServiceModal = ref(false);
    const selectedServiceParameter = ref<null | ServiceParameter>(null);
    const isWhatsappAccountConnected = ref(false);

    // Define service groups
    const serviceGroups = ref<ServiceGroup[]>([
      {
        title: 'storeParameters.services.groups.sendingChannels.title',
        description: 'storeParameters.services.groups.sendingChannels.description',
        icon: 'fal fa-paper-plane',
        services: [
          {
            name: 'email',
            title: 'storeParameters.services.emailServiceParameter.title',
            subtitle: 'storeParameters.services.emailServiceParameter.subtitle',
            component: 'EmailServiceParameter',
            icon: 'fal fa-envelope',
            modalWidth: '100rem',
          },
          {
            name: 'sms',
            title: 'storeParameters.services.smsServiceParameter.title',
            subtitle: 'storeParameters.services.smsServiceParameter.subtitle',
            component: 'SmsServiceParameter',
            icon: 'fal fa-message-sms',
            modalWidth: '100rem',
          },
          {
            name: 'push_notification',
            title: 'storeParameters.services.pushNotificationServiceParameter.title',
            subtitle: 'storeParameters.services.pushNotificationServiceParameter.subtitle',
            component: 'PushNotificationServiceParameter',
            icon: 'fal fa-bell',
            modalWidth: '100rem',
          },
          {
            name: 'whatsapp_business',
            title: 'storeParameters.services.whatsappBusinessParameter.title',
            subtitle: 'storeParameters.services.whatsappBusinessParameter.subtitle',
            manage: 'storeParameters.services.whatsappBusinessParameter.manage',
            component: 'WhatsappBusinessServiceParameter',
            icon: 'fa-brands fa-whatsapp',
            modalWidth: '64rem',
          },
          {
            name: 'rcs',
            title: 'storeParameters.services.rcsServiceParameter.title',
            subtitle: 'storeParameters.services.rcsServiceParameter.subtitle',
            manage: 'storeParameters.services.rcsServiceParameter.manage',
            component: 'RcsServiceParameter',
            icon: 'fa-light fa-message-lines',
            modalWidth: '64rem',
          },
        ],
      },
      {
        title: 'storeParameters.services.groups.generalSettings.title',
        description: 'storeParameters.services.groups.generalSettings.description',
        icon: 'fal fa-cog',
        services: [
          {
            name: 'important_links',
            title: 'storeParameters.services.importantLinksServiceParameter.title',
            subtitle: 'storeParameters.services.importantLinksServiceParameter.subtitle',
            component: 'ImportantLinksServiceParameter',
            icon: 'fal fa-link',
            modalWidth: '50rem',
          },
          {
            name: 'social_network',
            title: 'storeParameters.services.socialNetworkServiceParameter.title',
            subtitle: 'storeParameters.services.socialNetworkServiceParameter.subtitle',
            component: 'SocialNetworkServiceParameter',
            icon: 'fal fa-share-nodes',
            modalWidth: '75rem',
          },
          {
            name: 'subscription_page',
            title: 'storeParameters.services.subscriptionPageParameter.title',
            subtitle: 'storeParameters.services.subscriptionPageParameter.subtitle',
            component: 'SubscriptionPageServiceParameter',
            icon: 'fal fa-file-contract',
            modalWidth: '50rem',
          },
          {
            name: 'vouchers',
            title: 'storeParameters.services.voucherServiceParameter.title',
            subtitle: 'storeParameters.services.voucherServiceParameter.subtitle',
            component: 'VoucherServiceParameter',
            icon: 'fal fa-ticket',
            modalWidth: '50rem',
          },
          {
            name: 'ai_setting',
            title: 'storeParameters.services.aiServiceParameter.title',
            subtitle: 'storeParameters.services.aiServiceParameter.subtitle',
            component: 'AIServiceParameter',
            icon: 'fa-light fa-microchip-ai',
            modalWidth: '50rem',
          },
          {
            name: 'product_recommendations',
            title: 'storeParameters.services.productRecommendationServiceParameter.title',
            subtitle: 'storeParameters.services.productRecommendationServiceParameter.subtitle',
            component: 'ProductRecommendationServiceParameter',
            icon: 'fal fa-star',
            modalWidth: '50rem',
          },
          {
            name: 'utm_analytics',
            title: 'storeParameters.services.utmServiceParameter.title',
            subtitle: 'storeParameters.services.utmServiceParameter.subtitle',
            component: 'UtmServiceParameter',
            icon: 'fal fa-chart-line',
            modalWidth: '50rem',
          },
          {
            name: 'sales_association',
            title: 'storeParameters.services.salesAssociationServiceParameter.title',
            subtitle: 'storeParameters.services.salesAssociationServiceParameter.subtitle',
            component: 'SalesAssociationServiceParameter',
            icon: 'fal fa-cart-shopping',
            modalWidth: '60rem',
          },
        ],
      },
    ]);

    // Find service in all groups
    const findService = (serviceName: string): ServiceParameter | undefined => {
      let foundService: ServiceParameter | undefined;
      serviceGroups.value.some((group) => {
        foundService = group.services.find((item) => item.name === serviceName);
        return foundService !== undefined;
      });
      return foundService;
    };

    const handleConfigure = (serviceName: string) => {
      const service = findService(serviceName);
      if (service) {
        selectedServiceParameter.value = service;
        displayServiceModal.value = true;
      }
    };

    const handleClose = () => {
      displayServiceModal.value = false;
      selectedServiceParameter.value = null;
    };

    const serviceModalHeader = computed(() => {
      if (
        selectedServiceParameter.value
        && selectedServiceParameter.value.component === 'WhatsappBusinessServiceParameter'
        && isWhatsappAccountConnected.value
      ) {
        return t(selectedServiceParameter.value.manage);
      }
      return t(selectedServiceParameter.value?.subtitle ?? '');
    });

    function onWhatsappAccountConnected(value: boolean) {
      isWhatsappAccountConnected.value = value;
    }

    // Dynamic component loader
    const getServiceComponent = (componentName: string) => {
      return defineAsyncComponent(() => import(`@/views/shop/tabs/ServicesParameters/services/${componentName}.vue`)
        .catch(() => {
          // Fallback for components in subdirectories
          return import(`@/views/shop/tabs/ServicesParameters/services/${componentName}/${componentName}.vue`);
        }));
    };

    const getServiceFlag = getFeatureFlagForService;

    return {
      t,
      serviceGroups,
      getServiceFlag,
      selectedServiceParameter,
      displayServiceModal,
      handleConfigure,
      handleClose,
      serviceModalHeader,
      onWhatsappAccountConnected,
      isWhatsappAccountConnected,
      getServiceComponent,
    };
  },
});
